import React from 'react';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import RefreshIcon from '@material-ui/icons/Refresh';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Divider } from '@material-ui/core';
import { IconButton } from '@material-ui/core';
import ShareIcon from '@mui/icons-material/Share';
import Snackbar from '@mui/material/Snackbar';
import CloseIcon from '@mui/icons-material/Close';
import MUIButton from '@material-ui/core/Button';
import Accordion from '../Accordion';
import { QUICK_FILTERS } from './constants';
import Paper from '../Paper';
import Filter from '../Filter';
import Button from '../Button';
import { useStyles } from './styles';
import { DATA_TYPES } from '../Filter/constants';
import WdValidator from './../WdValidator/WdValidator';
import Chart from './Charts/Chart';
import useFilterUrl from '../../hooks/useFilterUrl';
import { resetChartFiltrationParameters } from '../../redux/slices/chartFilterSlice';
import { changeExpandState } from '../../redux/slices/categoryDataSlice';
import { resetAllFilters } from '../../redux/slices/filtersSlice';
import { resetHistory, resetMessages } from '../../redux/slices/chatBotSlice';
import Link from '../../components/Link';
import { BASENAME } from './../../utils/constants/general';
import pushToGTM, { shareFiltersGA4 } from '../../utils/analytics/gtm';
import { recordEvent } from '../../utils/analytics/local';
import { COLORS } from '../../styles/constants';
import HAND_ICON from '../../images/icons/NeedAGateDriver.svg';
import queryString from 'query-string';
import { CIDS_FOR_TRANSLATION } from '../ProductsTable/constants';
// import { ChatWindow } from '../ChatComponent/ChatWindow/ChatWindow';
// import ChatIcon from '@mui/icons-material/Chat';
// import Slide from '@material-ui/core/Slide';
// import { Box, Container } from '@mui/material';
import { getDomain } from '../../utils/config';
import { Menu, MenuItem } from '@material-ui/core';
import { URL } from '../../utils/constants/general';
import CATEGORY_HIERARCHY from '../../utils/constants/category';

const QuickFilters = ({ category, wdSupported, values, setValues }) => {
	const { t } = useTranslation(['main', 'category']);
	const classes = useStyles();
	const filters = useSelector(state => state.categoryData.filters);
	const filtersState = useSelector(state => state.filters.appliedState);
	const similarPartsForWPN = useSelector(state => state.filters.similarParts.wpn);
	const similarPartsForOPN = useSelector(state => state.filters.similarParts.opn);
	const similarPartsFiltersAutoSet = useSelector(state => state.filters.similarParts.autoSet);
	const chartFiltrationParameters = useSelector(state => state.chartFilters.filtrationParameters);
	const chartIsZoomed = useSelector(state => state.chartFilters.zoomState.isZoomed);
	const dispatch = useDispatch();
	const appliedStateOfQuickFilter = useSelector(state => state.filters.appliedState);
	const supplyTypeState = appliedStateOfQuickFilter?.pwcv?.value?.toString()?.substring(0, 2);
	const [isResetClicked, setIsResetClicked] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [open, setOpen] = useState(false);
	const isExpanded = useSelector(state => state.categoryData.isApplicationOrientedExpanded);
	const location = useLocation();
	const { resetURL, removeUtmParameters } = useFilterUrl();
	const [gateDriverMenuAnchorEl, setGateDriverMenuAnchorEl] = React.useState(null);
	// const [visibleChat, setVisibleChat] = useState(false);

	// const handleOnClick = () => {
	// 	setVisibleChat(!visibleChat);
	// };
	const domain = getDomain();

	useEffect(() => {
		setExpanded(isExpanded);
	}, [isExpanded]);

	const handleSnackbar = async () => {
		let currentFilters = queryString.parse(window.location.search);
		removeUtmParameters(currentFilters);
		const cleanedURL = `?${queryString.stringify(currentFilters, {
			encode: false
		})}`;
		const sharePath = window.location.host + window.location.pathname + encodeURI(cleanedURL);
		await navigator.clipboard.writeText(sharePath);
		setOpen(true);
		pushToGTM(shareFiltersGA4());
		recordEvent('SHARE_FILTERS', JSON.stringify({ topology: category.id }));
	};

	const handleSnackbarClose = () => {
		setOpen(false);
	};

	const QUICK_FILTERS_GROUPS = QUICK_FILTERS[category.id].filterGroups;
	const categoryDescription = CATEGORY_HIERARCHY.categories.find(item => item.id === category.id);
	const needGateDriverSupported = categoryDescription.needGatedDriverSupported;
	const note = t('category:' + category.id + '.note');
	const needAGateDriverTranslated = t('main:components.quickFilters.needGateDriver.gateDriver');
	const linkDescriptionTranslated = t('category:' + category.id + '.linkDescription');
	const linkDescription = linkDescriptionTranslated.includes('.linkDescription') ? '' : linkDescriptionTranslated;
	const linkHighVoltage = t('category:' + category.id + '.linkHighVoltage');
	const linkLowMediumVoltage = t('category:' + category.id + '.linkLowMediumVoltage');
	const linkSmallSignal = t('category:' + category.id + '.linkSmallSignal');
	const similarPartNote = similarPartsFiltersAutoSet
		? t('main:components.similarParts.similarPartsNote1') +
		  ': ' +
		  (similarPartsForWPN ? similarPartsForWPN : similarPartsForOPN) +
		  ' ' +
		  t('main:components.similarParts.similarPartsNote2')
		: t('main:components.similarParts.similarPartsNoteAfterFilterChange') +
		  ': ' +
		  (similarPartsForWPN ? similarPartsForWPN : similarPartsForOPN);

	const action = (
		<IconButton size="small" aria-label="close" color="inherit" onClick={handleSnackbarClose}>
			<CloseIcon fontSize="small" />
		</IconButton>
	);

	const determineSliderSpacing = (side, qfGroupFilters) =>
		qfGroupFilters.some(qFilter => {
			const filter = filters.find(filter => filter.cid === qFilter.cid);

			return (
				filter.type === DATA_TYPES.NUMERIC &&
				filter.constraints &&
				(side === 'left' ? filter.constraints.minOperator : filter.constraints.maxOperator)
			);
		});

	const handleChange = panel => (event, newExpanded) => {
		setExpanded(newExpanded ? panel : false);
		dispatch(changeExpandState(!expanded));
	};

	const resetFilters = () => {
		dispatch(resetAllFilters());
		dispatch(resetChartFiltrationParameters());
		dispatch(resetHistory());
		dispatch(resetMessages());
		resetURL();
		setIsResetClicked(true);
	};

	const getLabelForFilter = filter => {
		let filterLabel;
		if (filter.cid === 'slms') return filter.label;
		if (filter.unit === null || filter.unit === '-') {
			filterLabel = t(`filters.${filter.cid}`);
		} else {
			filterLabel = filter.label;
		}

		if (CIDS_FOR_TRANSLATION.includes(filter.cid)) {
			filterLabel = t(`filters.${filter.cid}`);
		} else if (filter.cid === 'voapo') {
			filterLabel = t(`filters.${filter.cid}`) + ' V<sub>OUT</sub>';
		}

		return filterLabel;
	};

	const getLongLabelForFilter = filter => {
		if (filter.longLabel === null) return null;
		return t(`components.quickFilters.longLabels.${filter.cid}`);
	};

	return (
		<div>
			<Paper
				padding={3}
				style={{
					paddingBottom: 16,
					position: 'relative'
				}}>
				<Typography variant="h5" className={QUICK_FILTERS[category.id].gutterTop === true && classes.gutterBottomTitle}>
					{t('main:components.quickFilters.title')}
				</Typography>
				<Grid container>
					<Grid container direction="column" spacing={1}>
						<Grid id="Quick_Filters__container" item container spacing={5}>
							{QUICK_FILTERS[category.id] &&
								QUICK_FILTERS_GROUPS.map((qFilterGroup, fgIndex) => {
									const leftSliderSpacing = determineSliderSpacing('left', qFilterGroup.filters);
									const rightSliderSpacing = determineSliderSpacing('right', qFilterGroup.filters);

									return (
										<Grid key={qFilterGroup.id + fgIndex + '-grid-item'} item>
											{qFilterGroup.title && <Typography variant="subtitle1">{qFilterGroup.title}</Typography>}

											{!chartIsZoomed &&
												qFilterGroup.filters.map((qFilter, fIndex) => {
													const filter = filters.find(f => f.cid === qFilter.cid);

													return filter ? (
														<div
															key={filter.cid + '-qfContainer'}
															className={clsx(
																classes.filter,
																{
																	[classes.filterGutterBottom]:
																		fIndex < qFilterGroup.filters.length - 1 && qFilter.gutterBottom === true
																},
																{
																	[classes.filterRight]: qFilter.align === 'right'
																}
															)}>
															<Filter
																categoryId={category.id}
																leftSliderSpacing={leftSliderSpacing}
																rightSliderSpacing={rightSliderSpacing}
																horizontal
																label={
																	filter.cid === 'vim' && supplyTypeState === 'DC'
																		? 'V<sub>IN<sub>DC</sub> (max)</sub> (V)'
																		: filter.cid === 'vim' && supplyTypeState === 'AC'
																		? 'V<sub>IN<sub>RMS</sub> (max)</sub> (V)'
																		: filter.cid === 'vin' && supplyTypeState === 'DC'
																		? 'V<sub>IN<sub>DC</sub> (min)</sub> (V)'
																		: filter.cid === 'vin' && supplyTypeState === 'AC'
																		? 'V<sub>IN<sub>RMS</sub> (min)</sub> (V)'
																		: getLabelForFilter(filter)
																}
																longLabel={getLongLabelForFilter(filter)}
																appliedLogic={filtersState[filter.cid].logic}
																appliedValue={filtersState[filter.cid].value}
																disabled={filtersState[filter.cid].disabled}
																cid={filter.cid}
																conditional={filter.conditional}
																constraints={filter.constraints || null}
																logic={filter.logic}
																values={filter.values}
																dataType={filter.type || null}
																filterType={qFilter.type}
																multiSelect={filter.multiSelect || null}
																order={filter.order || null}
																unit={filter.unit || null}
															/>
														</div>
													) : null;
												})}
										</Grid>
									);
								})}

							{/* <Container>
								<Grid container>
									<Grid item xs={12}>
										<Grid container alignItems="center" style={{ height: '38vh' }}>
											<Grid item xs={12} style={{ position: 'relative', height: '100%' }}>
												<Slide direction="left" timeout={150} in={visibleChat} mountOnEnter unmountOnExit>
													<Grid>
														<Typography variant="body1" color="textSecondary" className={classes.note}>
															{t('main:components.chatbot.disclaimer')}
														</Typography>
														<Box sx={{ position: 'absolute', height: '100%', width: '100%', zIndex: 2 }}>
															<ChatWindow category={category} />
														</Box>
													</Grid>
												</Slide>
												<Box sx={{ position: 'relative', zIndex: 1, height: '100%', width: '100%' }}>
													<Chart />
												</Box>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Container> */}
							<div className={classes.chartContainer}>
								<Chart />
							</div>

							{wdSupported && (
								<Grid>
									<div className={classes.webDesignerAccordion}>
										<Divider className={classes.divider} />
										<Accordion
											className={classes.accordion}
											label={t('main:components.quickFilters.applicationOriented.accordion')}
											defaultExpanded={false}
											expandIcon={<ExpandMoreIcon className={classes.expand_icon} />}
											expanded={expanded}
											onChange={handleChange(true)}>
											<WdValidator isResetClicked={isResetClicked} setIsResetClicked={setIsResetClicked} />
										</Accordion>
									</div>
								</Grid>
							)}
						</Grid>
						{(similarPartsForWPN || similarPartsForOPN) && (
							<Grid>
								<Typography variant="body1" color="primary" className={classes.note}>
									{similarPartNote}
								</Typography>
							</Grid>
						)}

						<Grid item container direction="column" spacing={2}>
							<Grid item>
								{location.pathname.includes('/wdValidated') ? (
									<Link style={{ textDecoration: 'none' }} href={BASENAME + '/power-supply/products'}>
										<Button color="secondary" bold onClick={resetFilters} startIcon={<RefreshIcon />} disabled={false}>
											{t('main:components.quickFilters.buttons.reset')}
										</Button>
									</Link>
								) : (
									<Grid item container spacing={3} style={{ marginTop: '5px' }}>
										<Grid item>
											<Button
												color="secondary"
												bold
												onClick={resetFilters}
												values={values}
												setValues={setValues}
												startIcon={<RefreshIcon />}
												disabled={
													!Object.keys(filtersState).some(cid => filtersState[cid].used === true) &&
													!chartFiltrationParameters.value
												}>
												{t('main:components.quickFilters.buttons.reset')}
											</Button>
										</Grid>

										<Grid item>
											{Object.keys(filtersState).some(cid => filtersState[cid].used === true) && (
												<MUIButton
													variant="contained"
													color="secondary"
													className={classes.shareButton}
													onClick={handleSnackbar}
													values={values}
													setValues={setValues}
													startIcon={<ShareIcon style={{ color: COLORS.PRIMARY }} />}>
													{t('main:components.shareFilters.message')}
												</MUIButton>
											)}
										</Grid>

										<Snackbar
											sx={{ height: '100%', top: '15px' }}
											anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
											open={open}
											autoHideDuration={3000}
											onClose={handleSnackbarClose}
											message={t('main:components.shareFilters.snackbar')}
											action={action}
										/>
									</Grid>
								)}
							</Grid>

							{/* <IconButton className={classes.chatIcon} onClick={handleOnClick} title="Chatbot">
								<ChatIcon sx={{ fontSize: 24 }} />
							</IconButton> */}

							{(note || linkDescription || needGateDriverSupported) && (
								<Grid item>
									{note && (
										<Typography variant="body2" color="textSecondary" className={classes.note}>
											{'\u2020' + note}
										</Typography>
									)}
									{linkDescription && (
										<>
											<Typography variant="body2" color="textSecondary" className={classes.note}>
												{linkDescription}
												<Link
													variant="body2"
													href={`https://www.${domain}/products/discrete-power-modules/mosfets/high-voltage-mosfets`}>
													{linkHighVoltage}
												</Link>
												{', '}
												<Link
													variant="body2"
													href={`https://www.${domain}/products/discrete-power-modules/mosfets/low-medium-voltage-mosfets`}>
													{linkLowMediumVoltage}
												</Link>
												{', '}
												<Link
													variant="body2"
													href={`https://www.${domain}/products/discrete-power-modules/mosfets/small-signal-mosfets`}>
													{linkSmallSignal}
												</Link>
											</Typography>
										</>
									)}

									{needGateDriverSupported && (
										<div className={classes.needgt}>
											<img src={HAND_ICON} alt="Need a Gate Driver?" className={classes.handIcon} />
											<Typography variant="body1" color="textPrimary">
												{t('main:components.quickFilters.needGateDriver.needgt')}
												{needGateDriverSupported === 'simple' && (
													<Link
														className={classes.crossSell}
														href={URL.NEED_A_GATE_DRIVER}
														target="_blank"
														rel="noopener noreferrer">
														{needAGateDriverTranslated}
													</Link>
												)}
												{needGateDriverSupported === 'advance' && (
													<Typography
														variant="body1"
														color="textPrimary"
														component="span"
														onClick={event => setGateDriverMenuAnchorEl(event.currentTarget)}
														className={classes.crossSell}
														role="button"
														tabIndex={0} // Makes it focusable
													>
														{needAGateDriverTranslated}
													</Typography>
												)}
											</Typography>
											<Menu
												id="simple-menu"
												anchorEl={gateDriverMenuAnchorEl}
												keepMounted
												open={Boolean(gateDriverMenuAnchorEl)}
												onClose={() => setGateDriverMenuAnchorEl(null)}
												classes={{ paper: classes.roundedMenu }}
												className={classes.gateDriverMenu}
												getContentAnchorEl={null} // Disable automatic anchoring
												anchorOrigin={{
													vertical: 'bottom',
													horizontal: 'right'
												}}
												transformOrigin={{
													vertical: 'top',
													horizontal: 'right'
												}}>
												<MenuItem onClick={() => setGateDriverMenuAnchorEl(null)}>
													<Link
														href={URL.NEED_A_GATE_DRIVER}
														className={classes.crossSell}
														target="_blank"
														rel="noopener noreferrer">
														{t('main:components.quickFilters.needGateDriver.galvanicInductiveIsolation')}
													</Link>
												</MenuItem>
												<MenuItem onClick={() => setGateDriverMenuAnchorEl(null)}>
													<Link
														href={URL.GATE_DRIVERS_OPTOCOUPLERS}
														className={classes.crossSell}
														target="_blank"
														rel="noopener noreferrer">
														{t('main:components.quickFilters.needGateDriver.galvanicOpticalIsolation')}
													</Link>
												</MenuItem>
											</Menu>
										</div>
									)}
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Paper>
		</div>
	);
};

QuickFilters.propTypes = {
	category: PropTypes.object.isRequired,
	wdSupported: PropTypes.bool.isRequired
};

export default QuickFilters;
